import { useRef } from "react";
import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { ArrowDown, Play } from "lucide-react";
import { LOGOS, TOTALS } from "@/data/content";
import { scrollToId } from "@/lib/lenis";
import { EASE, Star, Note } from "./Reveal";

const LINES = [
  { t: "Música,", c: "puffy-blue" },
  { t: "aventura e", c: "puffy-green" },
  { t: "família.", c: "puffy-red" },
];

const Headline = () => (
  <h1 className="text-[3.4rem] leading-[0.95] sm:text-7xl lg:text-[6.2rem]" data-testid="hero-headline" aria-label="Música, aventura e família.">
    {LINES.map((l, i) => (
      <span key={l.t} className="mask-line" aria-hidden="true">
        <motion.span
          className={`puffy ${l.c} inline-block`}
          initial={{ y: "115%", rotate: 4 }}
          animate={{ y: "0%", rotate: 0 }}
          transition={{ duration: 1.1, delay: 0.35 + i * 0.14, ease: EASE }}
        >
          {l.t}
        </motion.span>
      </span>
    ))}
  </h1>
);

const TiltLogo = () => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 140, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-18, 18]), { stiffness: 140, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };
  return (
    <div className="relative [perspective:1200px]" onMouseMove={onMove} onMouseLeave={reset}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative">
        <motion.img
          src={LOGOS.show}
          alt="Logo Pedro Clube Show"
          data-testid="hero-logo-tilt-image"
          initial={{ scale: 0.6, opacity: 0, rotate: -12 }}
          animate={{ scale: 1, opacity: 1, rotate: -3 }}
          transition={{ type: "spring", stiffness: 90, damping: 11, delay: 0.6 }}
          className="relative z-10 w-full drop-shadow-[0_30px_40px_rgba(31,63,151,0.35)]"
          style={{ transform: "translateZ(60px)" }}
        />
        <div className="absolute inset-[12%] -z-0 rounded-full bg-[var(--yellow)]/60 blur-3xl" style={{ transform: "translateZ(-40px)" }} />
      </motion.div>
    </div>
  );
};

const Floaters = ({ y1, y2 }) => (
  <>
    <motion.div style={{ y: y1 }} className="pointer-events-none absolute left-[4%] top-[18%] hidden sm:block">
      <Star className="floaty h-14 w-14" />
    </motion.div>
    <motion.div style={{ y: y2 }} className="pointer-events-none absolute right-[6%] top-[12%]">
      <Note className="floaty h-10 w-10 sm:h-14 sm:w-14" fill="var(--green)" />
    </motion.div>
    <motion.div style={{ y: y1 }} className="pointer-events-none absolute bottom-[14%] right-[42%] hidden lg:block">
      <Star className="floaty h-9 w-9" fill="var(--red)" />
    </motion.div>
  </>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const desktop = typeof window !== "undefined" && window.matchMedia("(min-width: 1024px)").matches;
  const logoY = useTransform(scrollYProgress, [0, 1], [0, desktop ? 160 : 0]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, desktop ? -60 : 0]);
  const y1 = useTransform(scrollYProgress, [0, 1], [0, -220]);
  const y2 = useTransform(scrollYProgress, [0, 1], [0, 120]);

  return (
    <section ref={ref} id="topo" className="relative overflow-hidden pb-16 pt-28 sm:pt-36" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-40 top-20 h-[28rem] w-[28rem] rounded-full bg-[var(--blue)]/15 blur-3xl" />
      <div className="pointer-events-none absolute -right-32 bottom-0 h-[26rem] w-[26rem] rounded-full bg-[var(--green)]/15 blur-3xl" />
      <Floaters y1={y1} y2={y2} />
      <div className="mx-auto grid max-w-6xl items-center gap-10 px-5 sm:px-8 lg:grid-cols-12">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-mono-label mb-6 inline-flex -rotate-2 items-center gap-2 rounded-full bg-[var(--ink)] px-4 py-2 text-[11px] uppercase tracking-[0.2em] text-white"
          >
            <span className="h-2 w-2 animate-pulse rounded-full bg-[var(--yellow)]" /> Conteúdo familiar & infantil
          </motion.span>
          <Headline />
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.9, ease: EASE }}
            className="mt-7 max-w-md text-base text-[var(--ink)]/75 sm:text-lg"
          >
            Somos Diego, Dani e o Pedro, de Porto Alegre. Criamos memórias, músicas e entretenimento seguro, autêntico e de alta qualidade para toda a família.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.05, ease: EASE }}
            className="mt-8 flex flex-wrap gap-3"
          >
            <button
              onClick={() => scrollToId("#canais")}
              data-testid="hero-cta-explore-channels"
              className="group inline-flex items-center gap-2 rounded-full bg-[var(--blue)] px-6 py-3.5 font-bold text-white shadow-[0_6px_0_var(--blue-deep)] transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-1 active:shadow-[0_2px_0_var(--blue-deep)]"
            >
              <Play className="h-4 w-4 fill-white" /> Ver nossos canais
            </button>
            <button
              onClick={() => scrollToId("#midia-kit")}
              data-testid="hero-cta-view-mediakit"
              className="inline-flex items-center gap-2 rounded-full border-2 border-[var(--ink)] bg-white px-6 py-3.5 font-bold text-[var(--ink)] transition-colors duration-300 hover:bg-[var(--yellow)]"
            >
              Mídia kit & números <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: logoY }} className="relative mx-auto w-full max-w-[420px] lg:col-span-5 lg:max-w-none">
          <TiltLogo />
        </motion.div>
      </div>
      <HeroStats />
    </section>
  );
};

const HeroStats = () => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.9, delay: 1.2, ease: EASE }}
    className="relative z-10 mx-auto mt-16 max-w-6xl px-5 sm:px-8 lg:mt-14"
    data-testid="hero-stats"
  >
    <span
      data-testid="hero-four-months-badge"
      className="font-mono-label mb-4 inline-flex rotate-1 items-center gap-2 rounded-full bg-[var(--yellow)] px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-[var(--ink)] shadow-[0_4px_0_#c58f0a]"
    >
      <Star className="h-3.5 w-3.5" fill="var(--red)" /> Tudo isso em apenas 4 meses
    </span>
    <dl className="grid grid-cols-3 gap-3">
      {TOTALS.map((s) => (
        <div key={s.l} className="border-t-2 border-[var(--ink)]/15 pt-3">
          <dt className="font-display text-2xl font-extrabold leading-none text-[var(--ink)] sm:text-4xl">{s.v}</dt>
          <dd className="mt-1 text-xs text-[var(--ink)]/70 sm:text-sm">{s.l}</dd>
        </div>
      ))}
    </dl>
  </motion.div>
);
